/**
 * ASCII Hero Engine - AsciiConcentricCircles (AsciiRippleRings) Class
 * An interactive concentric circle illustration with harmonically decaying
 * ring spacing and a staggered multi-ring mouse latency physics chain.
 * - Starts with a base circle stroke of radius R0.
 * - Each subsequent ring is larger by step 's', with 's' reduced by stepDecay (e.g. 10%).
 * - Staggered mouse latency: The smallest inner circle has lowest latency (fastest),
 *   while larger outer rings have progressively higher latency (slow, fluid trail).
 * - On spotlight hover: Reveals kinetic perimeter ASCII glyphs & luminous glow.
 */

import { Easings } from "../utils/easings.js";
import { parseColorToRgb } from "../utils/math.js";

export class AsciiConcentricCircles {
  constructor(options = {}) {
    this.name = options.name || "concentric-circles";
    this.type = "concentric-circles";

    // Positioning: 'portrait' (anchored to subject) or 'screen' (anchored to viewport 0..1)
    this.anchor = options.anchor || "portrait";
    this.u = options.u !== undefined ? options.u : 0.35;
    this.v = options.v !== undefined ? options.v : 0.65;
    this.normalized = options.normalized !== undefined ? options.normalized : true;
    this.x = options.x || 0;
    this.y = options.y || 0;
    this.worldX = 0;
    this.worldY = 0;

    // Concentric Geometry Parameters
    this.ringCount = options.ringCount || 7; // Number of concentric rings
    this.baseRadius = options.baseRadius || 22; // Radius of smallest inner circle
    this.initialStep = options.initialStep || 34; // Initial step 's' added for 2nd ring
    this.stepDecay = options.stepDecay !== undefined ? options.stepDecay : 0.1; // Reduction per ring (10%)

    // Calculate static radii for each ring with decaying step 's'
    this.radii = [];
    let currentR = this.baseRadius;
    let currentStep = this.initialStep;
    for (let i = 0; i < this.ringCount; i++) {
      this.radii.push(currentR);
      currentR += currentStep;
      currentStep *= 1.0 - this.stepDecay;
    }

    // Physics & Staggered Latency Chain
    this.baseLag = options.baseLag || 0.22; // Latency of smallest inner circle
    this.lagDecay = options.lagDecay !== undefined ? options.lagDecay : 0.22; // Latency decay factor per ring
    this.followMode = options.followMode || "magnetic"; // 'mouse' or 'magnetic'
    this.magneticPull = options.magneticPull !== undefined ? options.magneticPull : 0.4;

    this.latencies = [];
    for (let i = 0; i < this.ringCount; i++) {
      const lag = Math.max(0.018, this.baseLag * Math.pow(1.0 - this.lagDecay, i));
      this.latencies.push(lag);
    }

    // Individual ring center positions
    this.ringX = new Array(this.ringCount).fill(0);
    this.ringY = new Array(this.ringCount).fill(0);
    this.initialized = false;

    // Styling
    this.strokeColor = options.strokeColor || "#FF7120";
    this.strokeColorRGB = parseColorToRgb(this.strokeColor);
    this.strokeWidth = options.strokeWidth || 1.5;
    this.opacity = options.opacity !== undefined ? options.opacity : 0.9;
    this.depth = options.depth !== undefined ? options.depth : 0.75;
    this.layer = options.layer || "foreground";

    // ASCII typography on hover / spotlight
    this.asciiOnHover = options.asciiOnHover !== undefined ? options.asciiOnHover : true;
    this.asciiChars = options.asciiChars || ["·", "•", ":", "°", "*", "+", "x", "o", "§", "~"];
    this.fontSize = options.fontSize || 12;
    this.fontFamily = options.fontFamily || "Doto, DM Mono, monospace";
    this.fontWeight = options.fontWeight || "900";
    this.charSpacing = options.charSpacing || 24;

    // Spotlight state
    this.spotlightIntensity = 0;
    this.spotlightRadius = options.spotlightRadius || 260;
    this.spotlightAttackSpeed = options.spotlightAttackSpeed || 0.16;
    this.spotlightDecaySpeed = options.spotlightDecaySpeed || 0.048;

    this.tick = 0;
  }

  update(mouse, layout, config, parallaxX = 0, parallaxY = 0) {
    if (this.targetOpacity !== undefined && this.opacity !== this.targetOpacity) {
      this.opacity += (this.targetOpacity - this.opacity) * (this.fadeSpeed || 0.08);
      if (Math.abs(this.opacity - this.targetOpacity) < 0.005) {
        this.opacity = this.targetOpacity;
      }
    }

    const screenW = (config && config.width) || (typeof window !== "undefined" ? window.innerWidth : 1920);
    const screenH = (config && config.height) || (typeof window !== "undefined" ? window.innerHeight : 1080);

    // Compute anchor position in canvas coordinates
    if (this.anchor === "screen") {
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    } else if (this.normalized && layout && layout.renderW > 0) {
      this.worldX = layout.posX + this.u * layout.renderW;
      this.worldY = layout.posY + this.v * layout.renderH;
    } else if (this.x !== 0 || this.y !== 0) {
      this.worldX = this.x;
      this.worldY = this.y;
    } else {
      this.worldX = this.u * screenW;
      this.worldY = this.v * screenH;
    }

    const anchorX = this.worldX + parallaxX * this.depth;
    const anchorY = this.worldY + parallaxY * this.depth;

    // Initialize ring positions to anchor on first frame
    if (!this.initialized) {
      for (let i = 0; i < this.ringCount; i++) {
        this.ringX[i] = anchorX;
        this.ringY[i] = anchorY;
      }
      this.initialized = true;
    }

    // Determine target center point based on mouse interaction & followMode
    let targetX = anchorX;
    let targetY = anchorY;

    const isMouseActive = mouse && mouse.x !== -9999 && mouse.y !== -9999;

    if (isMouseActive) {
      if (this.followMode === "mouse") {
        targetX = mouse.x;
        targetY = mouse.y;
      } else {
        const dx = mouse.x - anchorX;
        const dy = mouse.y - anchorY;
        targetX = anchorX + dx * this.magneticPull;
        targetY = anchorY + dy * this.magneticPull;
      }
    }

    // Update each ring's position with its individual latency
    for (let i = 0; i < this.ringCount; i++) {
      const lag = this.latencies[i];
      this.ringX[i] += (targetX - this.ringX[i]) * lag;
      this.ringY[i] += (targetY - this.ringY[i]) * lag;
    }

    // Distance from inner circle to mouse for spotlight easing
    const innerDx = this.ringX[0] - (isMouseActive ? mouse.x : -9999);
    const innerDy = this.ringY[0] - (isMouseActive ? mouse.y : -9999);
    const dist = isMouseActive ? Math.sqrt(innerDx * innerDx + innerDy * innerDy) : 99999;

    const sRadius = this.spotlightRadius || (config && config.spotlightRadius) || 260;
    let targetSpotlight = 0;
    if (isMouseActive && dist < sRadius) {
      const normDist = dist / sRadius;
      targetSpotlight = Easings.easeOutQuart(1 - normDist);
    }

    if (targetSpotlight > this.spotlightIntensity) {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * this.spotlightAttackSpeed;
    } else {
      this.spotlightIntensity += (targetSpotlight - this.spotlightIntensity) * this.spotlightDecaySpeed;
    }

    this.tick += 0.025;
  }

  draw(ctx, config, parallaxX = 0, parallaxY = 0, layout = null, blurCanvas = null) {
    const itemOpacity = this.opacity !== undefined ? this.opacity : 1.0;
    if (itemOpacity <= 0.001) return;

    const [r, g, b] = this.strokeColorRGB;
    const isSpotlight = this.spotlightIntensity > 0.01;

    // 1. DRAW CONCENTRIC VECTOR RING STROKES
    for (let i = this.ringCount - 1; i >= 0; i--) {
      const radius = this.radii[i];
      const cx = this.ringX[i];
      const cy = this.ringY[i];

      const ringAlpha = (1.0 - (i / this.ringCount) * 0.35);
      const boostAlpha = Math.min(1.0, ringAlpha + this.spotlightIntensity * 0.4);

      ctx.save();
      ctx.globalAlpha = ctx.globalAlpha * itemOpacity;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.lineWidth = this.strokeWidth + this.spotlightIntensity * 0.5;
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${boostAlpha})`;
      ctx.stroke();

      // 2. KINETIC PERIMETER ASCII GLYPHS UNDER SPOTLIGHT
      if (this.asciiOnHover && isSpotlight && radius > 15) {
        const asciiAlpha = this.spotlightIntensity * 0.85;
        ctx.font = `${this.fontWeight} ${this.fontSize}px ${this.fontFamily}`;
        ctx.fillStyle = `rgba(255, 255, 255, ${asciiAlpha})`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        const circum = 2 * Math.PI * radius;
        const numGlyphs = Math.max(6, Math.floor(circum / this.charSpacing));
        const angleStep = (Math.PI * 2) / numGlyphs;
        const rotOffset = this.tick * (i % 2 === 0 ? 1 : -1) * (0.8 + i * 0.2);

        for (let k = 0; k < numGlyphs; k++) {
          const angle = k * angleStep + rotOffset;
          const gx = cx + Math.cos(angle) * radius;
          const gy = cy + Math.sin(angle) * radius;
          const char = this.asciiChars[(k + i * 2) % this.asciiChars.length];
          ctx.fillText(char, gx, gy);
        }
      }

      ctx.restore();
    }
  }
}

export const AsciiRippleRings = AsciiConcentricCircles;
